import { Box, Typography, makeStyles } from '@material-ui/core';
import SlideFrame from './SlideFrame';
import { CONTESTANT_COLORS } from './streamPalette';
import durationToString from '../../utils/durationToString';

const useStyles = makeStyles(theme => ({
    row: {
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        gap: theme.spacing(4),
        padding: theme.spacing(2, 0),
        '& + &': {
            borderTop: `1px solid ${theme.palette.divider}`
        }
    },
    value: {
        fontSize: '3rem',
        lineHeight: 1.1
    },
    // the numbers are white whoever they belong to - the coloured handles over
    // them say which column is whose. The better of the two is set in bold, so
    // neither of them is ever greyed out to make the point.
    better: {
        fontWeight: 'bold'
    },
    label: {
        minWidth: 220,
        textAlign: 'center'
    }
}));

const ACCEPTED = verdicts => verdicts?.find(verdict => verdict.verdict === 'OK');

const METRICS = [
    {
        label: 'Tasks solved',
        of: statistics => statistics.summary.solves,
        format: value => `${value}`,
        higherIsBetter: true
    },
    {
        label: 'Solves per match',
        of: statistics => statistics.summary.played === 0 ? null : statistics.summary.solves / statistics.summary.played,
        format: value => value.toFixed(1),
        higherIsBetter: true
    },
    {
        label: 'Accepted rate',
        of: statistics => statistics.summary.submissions === 0 ? null : (ACCEPTED(statistics.verdicts)?.percentage ?? 0),
        format: value => `${value.toFixed(1)}%`,
        higherIsBetter: true
    },
    {
        label: 'Time per solve',
        of: statistics => statistics.summary.timePerSolve,
        format: durationToString,
        higherIsBetter: false
    }
];

// The headline numbers of the two contestants, one metric to a row. The stronger
// of the two is left at full strength and the other dimmed, so the row can be
// read at a glance from a stream.
function HeadToHeadSlide(props) {
    const classes = useStyles();
    const both = [props.statistics1, props.statistics2];
    return (
        <SlideFrame eyebrow={props.tournamentName} matchLabel={props.matchLabel} title='Summary'>
            <Box display='grid' gridTemplateColumns='1fr auto 1fr' gridColumnGap={32} mb={2}>
                <Typography variant='h5' align='center' style={{ color: CONTESTANT_COLORS[0] }} noWrap>
                    {props.contestant1}
                </Typography>
                <div className={classes.label} />
                <Typography variant='h5' align='center' style={{ color: CONTESTANT_COLORS[1] }} noWrap>
                    {props.contestant2}
                </Typography>
            </Box>
            {METRICS.map(metric => {
                const values = both.map(statistics => statistics == null ? null : metric.of(statistics));
                const comparable = values.every(value => value != null);
                const better = !comparable || values[0] === values[1] ? -1 :
                    ((values[0] > values[1]) === metric.higherIsBetter ? 0 : 1);
                const valueOf = side => (
                    <Typography
                        align='center'
                        className={`${classes.value} ${better === side ? classes.better : ''}`}
                    >
                        {values[side] == null ? '—' : metric.format(values[side])}
                    </Typography>
                );
                return (
                    <div key={metric.label} className={classes.row}>
                        {valueOf(0)}
                        <Typography variant='h6' color='textSecondary' className={classes.label}>
                            {metric.label}
                        </Typography>
                        {valueOf(1)}
                    </div>
                );
            })}
        </SlideFrame>
    );
}

export default HeadToHeadSlide;
